import type {
  ManualChainEventKind,
  ManualChainPresentationEvent,
} from "../../gameplay/manual-chain/types.ts";

export interface FirstLoopAudioController {
  readonly unlocked: boolean;
  consume(events: readonly ManualChainPresentationEvent[]): void;
  unlock(): Promise<boolean>;
  setMuted(muted: boolean): void;
  dispose(): void;
}

interface FirstLoopAudioOptions {
  readonly unlockTarget?: EventTarget;
  readonly contextFactory?: () => AudioContext | null;
  readonly maxVoices?: number;
}

interface Note {
  readonly delay: number;
  readonly duration: number;
  readonly frequency: number;
  readonly gain: number;
  readonly type: OscillatorType;
  readonly detune?: number;
}

const DEFAULT_MAX_VOICES = 18;
const MASTER_GAIN = 0.28;
const LOOKAHEAD_SECONDS = 0.008;

const CUES: Readonly<Partial<Record<ManualChainEventKind, readonly Note[]>>> = Object.freeze({
  "harvest-committed": Object.freeze([
    note(0, 0.045, 196, 0.38, "triangle", -7),
    note(0.03, 0.08, 294, 0.28, "sine", 6),
    note(0.07, 0.1, 392, 0.18, "triangle", 4),
  ]),
  "stage-committed": Object.freeze([
    note(0, 0.055, 126, 0.4, "triangle"),
    note(0.04, 0.07, 189, 0.24, "sine"),
    note(0.09, 0.09, 252, 0.16, "triangle"),
  ]),
  "tend-started": Object.freeze([
    note(0, 0.05, 520, 0.26, "square", -15),
    note(0.06, 0.06, 430, 0.22, "square", 18),
    note(0.12, 0.055, 565, 0.2, "triangle"),
    note(0.18, 0.08, 650, 0.14, "sine"),
  ]),
  "tend-committed": Object.freeze([
    note(0, 0.06, 330, 0.26, "sine"),
    note(0.05, 0.075, 415.3, 0.24, "sine"),
    note(0.1, 0.1, 523.3, 0.22, "triangle"),
    note(0.18, 0.12, 659.3, 0.16, "sine"),
  ]),
  "collect-committed": Object.freeze([
    note(0, 0.05, 392, 0.26, "sine"),
    note(0.03, 0.1, 659.3, 0.22, "triangle"),
    note(0.1, 0.12, 784, 0.16, "sine"),
  ]),
  "sale-committed": Object.freeze([
    note(0, 0.05, 523.3, 0.28, "triangle"),
    note(0.045, 0.07, 659.3, 0.26, "triangle"),
    note(0.09, 0.09, 784, 0.24, "sine"),
    note(0.16, 0.14, 1046.5, 0.18, "sine"),
    note(0.22, 0.16, 1318.5, 0.12, "triangle"),
  ]),
  "pen-extension-ready": Object.freeze([
    note(0, 0.09, 392, 0.24, "sine"),
    note(0.08, 0.12, 523.3, 0.24, "sine"),
    note(0.16, 0.14, 659.3, 0.26, "triangle"),
    note(0.28, 0.2, 784, 0.2, "sine"),
    note(0.4, 0.22, 1046.5, 0.14, "triangle"),
  ]),
  "action-cancelled": Object.freeze([
    note(0, 0.07, 196, 0.13, "triangle"),
    note(0.055, 0.095, 155.6, 0.11, "sine"),
  ]),
});

export function createFirstLoopAudio(options: FirstLoopAudioOptions = {}): FirstLoopAudioController {
  const target = options.unlockTarget ?? (typeof window === "undefined" ? undefined : window);
  const maxVoices = Math.max(1, Math.trunc(options.maxVoices ?? DEFAULT_MAX_VOICES));
  const contextFactory = options.contextFactory ?? defaultContextFactory;
  const activeVoices = new Set<OscillatorNode>();
  const removals: Array<() => void> = [];

  let context: AudioContext | null = null;
  let master: GainNode | null = null;
  let disposed = false;
  let muted = false;
  let isUnlocked = false;
  let lastSequence = 0;

  const ensureContext = (): AudioContext | null => {
    if (disposed) return null;
    if (context !== null) return context;
    try {
      context = contextFactory();
      if (context === null) return null;
      master = context.createGain();
      master.gain.value = muted ? 0 : MASTER_GAIN;
      master.connect(context.destination);
      return context;
    } catch {
      context = null;
      master = null;
      return null;
    }
  };

  const unlock = async (): Promise<boolean> => {
    if (disposed) return false;
    const audio = ensureContext();
    if (audio === null) return false;
    try {
      if (audio.state === "suspended") await audio.resume();
      isUnlocked = audio.state === "running";
      return isUnlocked;
    } catch {
      return false;
    }
  };

  const onGesture = (): void => {
    void unlock();
  };

  if (target !== undefined) {
    for (const type of ["pointerdown", "touchstart", "keydown"] as const) {
      target.addEventListener(type, onGesture, { passive: true, once: false });
      removals.push(() => target.removeEventListener(type, onGesture));
    }
  }

  const playCue = (kind: ManualChainEventKind): void => {
    if (!isUnlocked || muted || disposed || context === null || master === null) return;
    const cue = CUES[kind];
    if (cue === undefined) return;
    const start = context.currentTime + LOOKAHEAD_SECONDS;

    for (const descriptor of cue) {
      if (activeVoices.size >= maxVoices) break;
      const oscillator = context.createOscillator();
      const envelope = context.createGain();
      oscillator.type = descriptor.type;
      oscillator.frequency.setValueAtTime(descriptor.frequency, start + descriptor.delay);
      oscillator.detune.setValueAtTime(descriptor.detune ?? 0, start + descriptor.delay);
      envelope.gain.setValueAtTime(0.0001, start + descriptor.delay);
      envelope.gain.exponentialRampToValueAtTime(
        Math.max(0.0001, descriptor.gain),
        start + descriptor.delay + Math.min(0.018, descriptor.duration * 0.35),
      );
      envelope.gain.exponentialRampToValueAtTime(
        0.0001,
        start + descriptor.delay + descriptor.duration,
      );
      oscillator.connect(envelope);
      envelope.connect(master);
      activeVoices.add(oscillator);
      oscillator.addEventListener("ended", () => {
        activeVoices.delete(oscillator);
        oscillator.disconnect();
        envelope.disconnect();
      }, { once: true });
      oscillator.start(start + descriptor.delay);
      oscillator.stop(start + descriptor.delay + descriptor.duration + 0.01);
    }
  };

  return {
    get unlocked(): boolean {
      return isUnlocked;
    },
    consume(events: readonly ManualChainPresentationEvent[]): void {
      if (disposed) return;
      for (const event of events) {
        if (!Number.isSafeInteger(event.sequence) || event.sequence <= lastSequence) continue;
        lastSequence = event.sequence;
        playCue(event.kind);
      }
    },
    unlock,
    setMuted(nextMuted: boolean): void {
      if (disposed) return;
      muted = nextMuted;
      if (master !== null && context !== null) {
        master.gain.setValueAtTime(muted ? 0 : MASTER_GAIN, context.currentTime);
      }
    },
    dispose(): void {
      if (disposed) return;
      disposed = true;
      for (const remove of removals.splice(0)) remove();
      for (const voice of [...activeVoices]) {
        try {
          voice.stop();
        } catch {
          // A voice may already have ended between the copy and stop call.
        }
      }
      activeVoices.clear();
      master?.disconnect();
      master = null;
      const ownedContext = context;
      context = null;
      if (ownedContext !== null && ownedContext.state !== "closed") {
        void ownedContext.close().catch(() => undefined);
      }
      isUnlocked = false;
    },
  };
}

function note(
  delay: number,
  duration: number,
  frequency: number,
  gain: number,
  type: OscillatorType,
  detune?: number,
): Note {
  return Object.freeze({ delay, duration, frequency, gain, type, ...(detune === undefined ? {} : { detune }) });
}

function defaultContextFactory(): AudioContext | null {
  if (typeof AudioContext === "undefined") return null;
  return new AudioContext({ latencyHint: "interactive" });
}
